import { useParams } from 'react-router-dom';
import { Star, Clock, Users, CheckCircle, PlayCircle } from 'lucide-react';

const CourseDetailPage = () => {
  const { id } = useParams();
  
  // Mock course data - in a real app, this would be fetched based on the ID
  const course = {
    id: parseInt(id || '1'),
    title: 'React Development Masterclass',
    description: 'Master React development from the ground up with this comprehensive course covering hooks, context, state management, and modern React patterns.',
    longDescription: 'This comprehensive React course is designed for developers who want to master modern React development. You\'ll learn everything from basic components to advanced patterns like compound components, render props, and custom hooks. The course includes hands-on projects, real-world examples, and best practices used by industry professionals.',
    image: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=800&h=400&fit=crop',
    price: '$99',
    originalPrice: '$149',
    rating: 4.9,
    students: 1250,
    duration: '12 hours',
    level: 'Intermediate',
    instructor: {
      name: 'Sarah Johnson',
      title: 'Senior React Developer',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face',
      bio: 'Sarah is a senior frontend developer with 8+ years of experience building React applications at top tech companies.',
    },
    curriculum: [
      {
        title: 'Introduction to React',
        lessons: [
          'What is React?',
          'Setting up your development environment',
          'Your first React component',
          'JSX fundamentals',
        ],
      },
      {
        title: 'Components and Props',
        lessons: [
          'Functional vs Class components',
          'Props and prop types',
          'Component composition',
          'Children prop',
        ],
      },
      {
        title: 'State and Event Handling',
        lessons: [
          'useState hook',
          'Event handling in React',
          'Controlled vs uncontrolled components',
          'Form handling',
        ],
      },
      {
        title: 'Advanced Hooks',
        lessons: [
          'useEffect hook',
          'useContext hook',
          'useReducer hook',
          'Custom hooks',
        ],
      },
      {
        title: 'State Management',
        lessons: [
          'Context API',
          'Redux fundamentals',
          'Redux Toolkit',
          'When to use what',
        ],
      },
    ],
    features: [
      '12 hours of video content',
      'Hands-on coding exercises',
      'Real-world projects',
      'Certificate of completion',
      'Lifetime access',
      'Mobile and desktop access',
    ],
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Main Content */}
        <div className="lg:col-span-2">
          {/* Hero Image */}
          <img
            src={course.image}
            alt={course.title}
            className="w-full h-64 md:h-80 object-cover rounded-lg mb-8"
          />

          {/* Course Info */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              {course.title}
            </h1>
            <p className="text-xl text-secondary-600 mb-6">
              {course.description}
            </p>

            <div className="flex flex-wrap items-center gap-6 text-sm text-secondary-500 mb-6">
              <div className="flex items-center">
                <Star className="h-5 w-5 text-yellow-400 mr-1" aria-label="Star icon" />
                {course.rating} ({course.students} students)
              </div>
              <div className="flex items-center">
                <Clock className="h-5 w-5 mr-1" aria-label="Clock icon" />
                {course.duration}
              </div>
              <div className="flex items-center">
                <Users className="h-5 w-5 mr-1" aria-label="Users icon" />
                {course.level}
              </div>
            </div>
          </div>

          {/* What you'll learn */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-secondary-900 mb-4">
              What you'll learn
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {course.features.map((feature, index) => (
                <div key={index} className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5" aria-label="CheckCircle icon" />
                  <span className="text-secondary-600">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Course Content */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-secondary-900 mb-4">
              Course Content
            </h2>
            <div className="space-y-4">
              {course.curriculum.map((section, index) => (
                <div key={index} className="border border-secondary-200 rounded-lg">
                  <div className="bg-secondary-50 px-6 py-4 border-b border-secondary-200">
                    <h3 className="font-semibold text-secondary-900">
                      {section.title}
                    </h3>
                  </div>
                  <div className="p-6">
                    <ul className="space-y-2">
                      {section.lessons.map((lesson, lessonIndex) => (
                        <li key={lessonIndex} className="flex items-center">
                          <PlayCircle className="h-4 w-4 text-primary-600 mr-3" aria-label="PlayCircle icon" />
                          <span className="text-secondary-600">{lesson}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* About the Instructor */}
          <div>
            <h2 className="text-2xl font-bold text-secondary-900 mb-6">
              About the Instructor
            </h2>
            <div className="flex items-start space-x-4">
              <img
                src={course.instructor.image}
                alt={course.instructor.name}
                className="w-16 h-16 rounded-full object-cover"
              />
              <div>
                <h3 className="text-xl font-semibold text-secondary-900">
                  {course.instructor.name}
                </h3>
                <p className="text-primary-600 mb-2">{course.instructor.title}</p>
                <p className="text-secondary-600">{course.instructor.bio}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-secondary-200 rounded-lg p-6 sticky top-24">
            {/* Pricing */}
            <div className="text-center mb-6">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <span className="text-3xl font-bold text-primary-600">
                  {course.price}
                </span>
                <span className="text-lg text-secondary-400 line-through">
                  {course.originalPrice}
                </span>
              </div>
              <p className="text-sm text-green-600 font-medium">Save 33%</p>
            </div>

            {/* Enroll Button */}
            <button className="w-full bg-primary-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary-700 transition-colors mb-4">
              Enroll Now
            </button>

            {/* Course Features */}
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-secondary-600">Duration:</span>
                <span className="font-medium">{course.duration}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-600">Level:</span>
                <span className="font-medium">{course.level}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-600">Students:</span>
                <span className="font-medium">{course.students}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-600">Access:</span>
                <span className="font-medium">Lifetime</span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary-600">Certificate:</span>
                <span className="font-medium">Yes</span>
              </div>
            </div>

            {/* 30-day guarantee */}
            <div className="mt-6 p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-800 text-center">
                30-day money-back guarantee
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetailPage;